import React from "react";
import "../assets/css/ResultsMobile.css";

function normalizeMetaChip(chip) {
  if (chip == null || chip === false) return null;
  if (typeof chip === "string" || typeof chip === "number") {
    const label = String(chip).trim();
    return label ? { label, tone: "neutral" } : null;
  }
  if (typeof chip === "object") {
    const label = String(chip.label ?? chip.text ?? "").trim();
    if (!label && !chip.icon) return null;
    return {
      label,
      icon: chip.icon || null,
      title: chip.title || null,
      tone: chip.tone || chip.variant || "neutral",
      status: Boolean(chip.status),
    };
  }
  return null;
}

/**
 * Native-style result card for compact viewports.
 * Desktop tables stay as-is; this is shown only under .co-mobile-results.
 */
export function ResultsMobileCard({
  title,
  titleIcon,
  subtitle,
  meta = [],
  fields = [],
  actions,
  onOpen,
  tone,
  fieldsLayout,
  children,
}) {
  const openable = typeof onOpen === "function";
  const chips = (Array.isArray(meta) ? meta : []).map(normalizeMetaChip).filter(Boolean);
  // Status chips (non-info, or flagged `status`) sit right of the name; business/type chips under it
  const isHeaderChip = (c) => c.status || c.tone !== "info";
  const statusChips = chips.filter(isHeaderChip);
  const secondaryChips = chips.filter((c) => !isHeaderChip(c));

  const secondary =
    secondaryChips.length > 0 ? (
      <div className="co-mcard__meta co-mcard__meta--secondary">
        {secondaryChips.map((chip, i) => (
          <span
            key={`${chip.label}-sec-${i}`}
            className={`co-mchip co-mchip--${chip.tone}${chip.icon ? " co-mchip--icon" : ""}`}
          >
            {chip.icon}
            {chip.label}
          </span>
        ))}
      </div>
    ) : null;

  const header = (
    <div className="co-mcard__header">
      {titleIcon ? (
        <span className="co-mcard__title-icon" aria-hidden="true">
          {titleIcon}
        </span>
      ) : null}
      {openable ? (
        <button type="button" className="co-mcard__title" onClick={onOpen}>
          {title || "—"}
        </button>
      ) : (
        <h4 className="co-mcard__title co-mcard__title--static">{title || "—"}</h4>
      )}

      {statusChips.length > 0 ? (
        <div className="co-mcard__meta" aria-label="Status">
          {statusChips.map((chip, i) => (
            <span
              key={`${chip.label}-${i}`}
              className={`co-mchip co-mchip--${chip.tone}${chip.icon ? " co-mchip--icon" : ""}`}
              title={chip.title || undefined}
              aria-label={chip.title || undefined}
            >
              {chip.icon}
              {chip.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <article className={`co-mcard${tone ? ` co-mcard--${tone}` : ""}${titleIcon ? " co-mcard--banded" : ""}`}>
      <div className="co-mcard__body">
        {titleIcon ? <div className="co-mcard__band">{header}</div> : header}
        {secondary}

        {subtitle ? <p className="co-mcard__subtitle">{subtitle}</p> : null}

        {fields.length > 0 ? (
          <dl
            className={`co-mcard__fields${
              fieldsLayout === "stacked" ? " co-mcard__fields--stacked" : ""
            }`}
          >
            {fields
              .filter((f) => f && f.value != null && String(f.value).trim() !== "" && f.value !== "—")
              .map((f) => (
                <div key={f.label} className="co-mcard__field">
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
          </dl>
        ) : null}

        {children}
      </div>

      {actions ? <div className="co-mcard__actions">{actions}</div> : null}
    </article>
  );
}

export function ResultsMobileList({
  children,
  empty,
  loading,
  loadingLabel = "Loading…",
  emptyLabel = "No results found.",
  className = "",
}) {
  if (loading) {
    return (
      <div className={`co-mobile-results${className ? ` ${className}` : ""}`} aria-busy="true">
        <p className="co-mobile-results__state">{loadingLabel}</p>
      </div>
    );
  }

  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) {
    return (
      <div className={`co-mobile-results${className ? ` ${className}` : ""}`}>
        <p className="co-mobile-results__state">{empty || emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className={`co-mobile-results${className ? ` ${className}` : ""}`}>
      <div className="co-mobile-results__list">{items}</div>
    </div>
  );
}

export default ResultsMobileList;
